/**
 * Renders the schema-graph block as a layered lineage map:
 * raw source tabs (left) → canonical entities (middle) → the output loan tape
 * (right). Built as a hand-laid SVG so the source→canonical→tape flow is
 * explicit: nodes are placed in fixed left→right tiers, provenance edges are
 * curved connectors labelled with field coverage, and entity↔entity join keys
 * are drawn as dashed arcs within the canonical column. The loan-grain "spine"
 * entity (the most-joined one) is emphasised, and hovering any node dims
 * everything outside its connected lineage path.
 */

import React, { useMemo, useState } from "react";
import type { SchemaGraphEdge, SchemaGraphNode } from "./Chat/Chat.types";

type Tier = SchemaGraphNode["category"];

const TIER: Record<
	Tier,
	{ label: string; fill: string; ink: string; stroke: string; ring: string }
> = {
	raw: { label: "Raw source tabs", fill: "#F1F5F9", ink: "#475569", stroke: "#CBD5E1", ring: "#94A3B8" },
	entity: { label: "Standardized fields", fill: "#008782", ink: "#FFFFFF", stroke: "#008782", ring: "#004C4B" },
	output: { label: "Loan tape", fill: "#0F3D3A", ink: "#EAFFF9", stroke: "#0F3D3A", ring: "#004C4B" },
};
const TIER_ORDER: Tier[] = ["raw", "entity", "output"];

const C = {
	flow: "#CBD5E1",
	flowInk: "#94A3B8",
	join: "#008782",
	label: "#6B7280",
	joinLabel: "#0F766E",
	mutedFill: "#F8FAFC",
	mutedStroke: "#CBD5E1",
	mutedInk: "#94A3B8",
};

// Redundant application-key join labels we don't surface on the map (the dashed
// arc still shows the relationship; the text is noise).
const HIDDEN_JOIN_LABELS = new Set([
	"application_ref = application_ref",
	"application_id = application_ref",
	"loan_id = loan_id",
]);

// Entities kept out of the standardized layer. Dropping the node also drops its
// edges, so its source tab (Vehicle & Collateral) falls through to the "not
// mapped" (greyed) treatment automatically.
const EXCLUDED_ENTITY = /collateral/i;

// ---- geometry (SVG user units; the <svg> scales to its container) ----
const BOX_H = 56;
const V_GAP = 26;
const PAD_Y = 20;
const COL: Record<Tier, { x: number; w: number }> = {
	raw: { x: 16, w: 196 },
	entity: { x: 344, w: 196 },
	output: { x: 700, w: 204 },
};

interface SchemaGraphBlockViewProps {
	title?: string;
	nodes: SchemaGraphNode[];
	edges: SchemaGraphEdge[];
	/** Render without the outer card + title (a parent already provides them). */
	bare?: boolean;
	/** Chart height (px number or any CSS length). Defaults to 360px. */
	height?: number | string;
}

type Placed = SchemaGraphNode & {
	cx: number;
	cy: number;
	x: number;
	w: number;
	subtitle: string;
	unmapped: boolean;
};

/** The bit of ``detail`` worth showing under the node name. */
function subtitleFor(n: SchemaGraphNode): string {
	if (!n.detail) return "";
	if (n.category === "output") return n.detail; // "500 loans · 33 columns"
	if (n.category === "entity") return n.detail.split(" · ")[0]; // the grain
	return ""; // raw tabs carry a generic sentence — keep the box clean
}

/** Horizontal cubic between the right edge of one box and the left edge of another. */
function flowPath(sx: number, sy: number, tx: number, ty: number): string {
	const mx = (sx + tx) / 2;
	return `M${sx},${sy} C${mx},${sy} ${mx},${ty} ${tx},${ty}`;
}

const SchemaGraphBlockView: React.FC<SchemaGraphBlockViewProps> = ({
	title,
	nodes,
	edges,
	bare = false,
	height = 360,
}) => {
	const [hover, setHover] = useState<string | null>(null);

	const { placed, viewH, viewW, spineId, flowEdges, joinEdges, keepFor } = useMemo(() => {
		// The output "Loan tape" node is intentionally not rendered — the map
		// stops at the standardized fields. The collateral entity is also
		// excluded (its source tab then greys out as "not mapped").
		const visibleNodes = nodes.filter(
			(n) =>
				n.category !== "output" &&
				!(n.category === "entity" && EXCLUDED_ENTITY.test(n.name)),
		);
		const byId = new Map(visibleNodes.map((n) => [n.id, n]));

		// Lay each tier out in its column, vertically centred.
		const groups = TIER_ORDER.map((t) => visibleNodes.filter((n) => n.category === t));
		const maxCount = Math.max(1, ...groups.map((g) => g.length));
		const vh = PAD_Y * 2 + maxCount * (BOX_H + V_GAP) - V_GAP;

		const placedMap = new Map<string, Placed>();
		groups.forEach((g, ti) => {
			const t = TIER_ORDER[ti];
			const { x, w } = COL[t];
			const blockH = g.length * (BOX_H + V_GAP) - V_GAP;
			const startY = (vh - blockH) / 2;
			g.forEach((n, i) => {
				const y = startY + i * (BOX_H + V_GAP);
				placedMap.set(n.id, {
					...n,
					x,
					w,
					cx: x + w / 2,
					cy: y + BOX_H / 2,
					subtitle: subtitleFor(n),
					unmapped: false,
				});
			});
		});

		// Split edges: entity↔entity are join keys; everything else is flow.
		const isEntity = (id: string) => byId.get(id)?.category === "entity";
		const flows: SchemaGraphEdge[] = [];
		const joins: SchemaGraphEdge[] = [];
		for (const e of edges) {
			if (!placedMap.has(e.source) || !placedMap.has(e.target)) continue;
			(isEntity(e.source) && isEntity(e.target) ? joins : flows).push(e);
		}

		// Spine = entity with the most join relationships (the loan-grain hub).
		const degree = new Map<string, number>();
		for (const e of joins) {
			degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
			degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
		}
		let spine: string | null = null;
		let best = -1;
		for (const [id, d] of degree) {
			if (d > best) {
				best = d;
				spine = id;
			}
		}

		// Adjacency over ALL edges → a node's full lineage path (both directions).
		const adj = new Map<string, Set<string>>();
		const link = (a: string, b: string) => {
			(adj.get(a) ?? adj.set(a, new Set()).get(a)!).add(b);
			(adj.get(b) ?? adj.set(b, new Set()).get(b)!).add(a);
		};
		[...flows, ...joins].forEach((e) => link(e.source, e.target));

		// A raw tab with no incident edge is present in the source file but not
		// mapped into the standardized schema — render it as a muted stub.
		for (const p of placedMap.values()) {
			if (p.category === "raw" && !adj.has(p.id)) {
				p.unmapped = true;
				p.subtitle = "not mapped into schema";
			}
		}

		const keepFor = (id: string): Set<string> => {
			const seen = new Set([id]);
			const q = [id];
			while (q.length) {
				const cur = q.shift()!;
				for (const nb of adj.get(cur) ?? []) {
					if (!seen.has(nb)) {
						seen.add(nb);
						q.push(nb);
					}
				}
			}
			return seen;
		};

		// Trim the viewBox to the rightmost rendered node so removing the output
		// column doesn't leave a wide empty gutter on the right.
		const maxX = Math.max(
			COL.raw.x + COL.raw.w,
			...[...placedMap.values()].map((n) => n.x + n.w),
		);

		return {
			placed: placedMap,
			viewH: vh,
			viewW: maxX + 16,
			spineId: spine,
			flowEdges: flows,
			joinEdges: joins,
			keepFor,
		};
	}, [nodes, edges]);

	const keep = useMemo(() => (hover ? keepFor(hover) : null), [hover, keepFor]);
	const nodeOpacity = (id: string) => (keep && !keep.has(id) ? 0.12 : 1);
	const edgeOpacity = (a: string, b: string) =>
		keep && !(keep.has(a) && keep.has(b)) ? 0.08 : 1;

	if (nodes.length === 0) return null;

	const hasUnmapped = [...placed.values()].some((n) => n.unmapped);
	const presentTiers = TIER_ORDER.filter((t) =>
		[...placed.values()].some((n) => n.category === t),
	);

	const graph = (
		<svg
			viewBox={`0 0 ${viewW} ${viewH}`}
			width={viewW}
			height={viewH}
			preserveAspectRatio="xMidYMid meet"
			className="max-w-full"
			style={{ maxHeight: height }}
			role="img"
			aria-label="Schema lineage: raw source tabs to canonical entities to the loan tape"
		>
			<defs>
				<filter id="node-shadow" x="-20%" y="-30%" width="140%" height="170%">
					<feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#0F172A" floodOpacity="0.14" />
				</filter>
			</defs>
			{/* flow edges: raw → entity, spine entity → tape */}
			{flowEdges.map((e, i) => {
				const s = placed.get(e.source)!;
				const t = placed.get(e.target)!;
				const toOutput = t.category === "output";
				const sx = s.x + s.w;
				const tx = t.x;
				const d = flowPath(sx, s.cy, tx, t.cy);
				const op = edgeOpacity(e.source, e.target);
				return (
					<g key={`f${i}`} className="transition-opacity duration-150" opacity={op}>
						<path
							d={d}
							fill="none"
							stroke={toOutput ? C.join : C.flow}
							strokeWidth={toOutput ? 2 : 1.5}
							strokeOpacity={toOutput ? 0.6 : 1}
						/>
						{e.label ? (
							<text
								x={(sx + tx) / 2}
								y={(s.cy + t.cy) / 2 - 6}
								textAnchor="middle"
								fontSize={10}
								fontFamily="ui-monospace, monospace"
								fill={C.label}
							>
								{e.label}
							</text>
						) : null}
					</g>
				);
			})}

			{/* join keys: dashed arcs bowing left of the canonical column */}
			{joinEdges.map((e, i) => {
				const s = placed.get(e.source)!;
				const t = placed.get(e.target)!;
				const x = s.x; // left edge of the entity column
				const midY = (s.cy + t.cy) / 2;
				const bow = Math.min(-26, -Math.abs(t.cy - s.cy) * 0.22);
				const d = `M${x},${s.cy} C${x + bow},${midY} ${x + bow},${midY} ${x},${t.cy}`;
				const op = edgeOpacity(e.source, e.target);
				// Label sits just above the target entity box (left-aligned to the
				// column) so it never runs off the left edge or over a node.
				const below = t.cy >= s.cy;
				const labelNode = below ? t : s;
				return (
					<g key={`j${i}`} className="transition-opacity duration-150" opacity={op}>
						<path d={d} fill="none" stroke={C.join} strokeWidth={1.2} strokeDasharray="3 4" strokeOpacity={0.5} />
						{e.label && !HIDDEN_JOIN_LABELS.has(e.label) ? (
							<text
								x={labelNode.x + 4}
								y={labelNode.cy - BOX_H / 2 - 7}
								textAnchor="start"
								fontSize={9.5}
								fontFamily="ui-monospace, monospace"
								fontWeight={600}
								fill={C.joinLabel}
							>
								{e.label}
							</text>
						) : null}
					</g>
				);
			})}

			{/* nodes */}
			{[...placed.values()].map((n) => {
				const tier = TIER[n.category];
				const isSpine = n.id === spineId;
				const y = n.cy - BOX_H / 2;
				const displayName = n.name.replace(/canonical/gi, "standardized");
				const ink = n.unmapped ? C.mutedInk : tier.ink;
				return (
					<g
						key={n.id}
						className="cursor-pointer transition-opacity duration-150"
						opacity={nodeOpacity(n.id)}
						onMouseEnter={() => setHover(n.id)}
						onMouseLeave={() => setHover(null)}
					>
						<title>{n.detail ? `${displayName} — ${n.detail}` : displayName}</title>
						<rect
							x={n.x}
							y={y}
							width={n.w}
							height={BOX_H}
							rx={12}
							fill={n.unmapped ? C.mutedFill : tier.fill}
							stroke={n.unmapped ? C.mutedStroke : isSpine ? tier.ring : tier.stroke}
							strokeWidth={isSpine ? 2.5 : 1.2}
							strokeDasharray={n.unmapped ? "4 4" : undefined}
							filter={n.unmapped ? undefined : "url(#node-shadow)"}
						/>
						<text
							x={n.x + 14}
							y={n.subtitle ? y + 24 : y + BOX_H / 2 + 4}
							fontSize={13}
							fontWeight={600}
							fill={ink}
						>
							{displayName}
						</text>
						{n.subtitle ? (
							<text
								x={n.x + 14}
								y={y + 42}
								fontSize={10}
								fontFamily="ui-monospace, monospace"
								fill={ink}
								fillOpacity={n.unmapped ? 0.9 : 0.75}
							>
								{n.subtitle.length > 30 ? `${n.subtitle.slice(0, 29)}…` : n.subtitle}
							</text>
						) : null}
					</g>
				);
			})}
		</svg>
	);

	const legend = (
		<div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-2">
			{presentTiers.map((t) => (
				<div key={t} className="flex items-center gap-1.5">
					<span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TIER[t].fill }} />
					<span className="text-xs text-text-muted">{TIER[t].label}</span>
				</div>
			))}
			{joinEdges.length > 0 ? (
				<div className="flex items-center gap-1.5">
					<span className="inline-block w-5 border-t border-dashed" style={{ borderColor: C.join }} />
					<span className="text-xs text-text-muted">Join key (relates raw tabs)</span>
				</div>
			) : null}
			{hasUnmapped ? (
				<div className="flex items-center gap-1.5">
					<span
						className="inline-block w-2.5 h-2.5 rounded-full border border-dashed"
						style={{ backgroundColor: C.mutedFill, borderColor: C.mutedStroke }}
					/>
					<span className="text-xs text-text-muted">Not mapped into schema</span>
				</div>
			) : null}
		</div>
	);

	const body = (
		<>
			<div className="text-xs text-text-muted mb-2">
				Raw file tabs → standardized fields → clean loan tape. Solid edges show field coverage and the flow into
				the tape; dashed edges are the join keys that relate the raw tabs. Hover any node to trace its full lineage.
			</div>
			{legend}
			<div className="w-full flex justify-center">{graph}</div>
		</>
	);

	if (bare) return body;

	return (
		<div className="rounded-lg border border-gray-200 bg-white p-4 my-3">
			<div className="text-sm font-medium text-text-strong mb-0.5">{title ?? "Schema & lineage map"}</div>
			{body}
		</div>
	);
};

export default SchemaGraphBlockView;
